import React, { useState, useCallback, useMemo } from 'react';
import {
  ReloadOutlined,
  CustomerServiceOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';
import {
  RetryWrapper,
  ErrorOverlay,
  LoadingOverlay,
  ActionButton,
  ContactButton,
  ErrorWrapper,
} from './styled';

interface MetricRetryProps {
  metricKey: string;
  isError: boolean;
  isLoading: boolean;
  onRetry: () => void;
  children: React.ReactNode;
  globalRetryAttempts?: number;
  globalRetryExceeded?: boolean;
}

const MAX_RETRY_ATTEMPTS = 3;

const MetricRetry: React.FC<MetricRetryProps> = ({
  metricKey,
  isError,
  isLoading,
  onRetry,
  children,
  globalRetryAttempts = 0,
  globalRetryExceeded = false,
}) => {
  const handleRetry = useCallback(() => {
    onRetry();
  }, [onRetry]);

  const handleContactSupport = useCallback(() => {
    window.top?.postMessage({ type: 'customer support' }, '*');
  }, []);

  // If there's an error and no loading, show retry/contact support overlay
  if (isError && !isLoading) {
    return (
      <RetryWrapper>
        {children}
        <ErrorOverlay>
          {globalRetryExceeded ? (
            <ErrorWrapper>
              <div>Something went wrong</div>
              <ContactButton
                type="link"
                icon={<CustomerServiceOutlined />}
                onClick={handleContactSupport}
                size="small"
              >
                Contact
              </ContactButton>
            </ErrorWrapper>
          ) : (
            <ErrorWrapper>
              <div>
                <WarningOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
                Error Loading data.
              </div>
              <ActionButton
                type="primary"
                ghost
                icon={<ReloadOutlined />}
                onClick={handleRetry}
                size="small"
                loading={isLoading}
              >
                Retry
              </ActionButton>
            </ErrorWrapper>
          )}
        </ErrorOverlay>
      </RetryWrapper>
    );
  }

  // Show loading overlay when loading
  if (isLoading) {
    return (
      <RetryWrapper>
        {children}
        <LoadingOverlay>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />
        </LoadingOverlay>
      </RetryWrapper>
    );
  }

  // Normal rendering when no error or loading
  return <>{children}</>;
};

export default MetricRetry;
